/**
 * Topology re-evaluation after a geometry change (drag, batch move).
 *
 * REWRITTEN 2026-09-02 onto deriveTopology + the stored-authoritative write
 * policy (owner decision): blanks fill silently with provenance; a stored
 * value that differs from the fresh derive is NEVER silently overwritten —
 * all conflicts of one gesture batch into ONE keep-or-update dialog. The
 * pre-rewrite behavior (overwrite every field unconditionally) is exactly
 * what destroyed typed corrections on a 5cm nudge.
 *
 * Layouts: ALL of them — the derive runs its own per-indication layout
 * contest (T-09); the active-layout alias is never consulted.
 */

import { useProjectStore } from '../../stores/projectStore';
import { useUIStore } from '../../stores/uiStore';
import {
  planTopologyUpdate,
  humanEditProvenance,
  type TopologyApplyPlan,
} from './applyDerivedTopology';
import type { SiteLayout } from '../layout/types';

function allLayouts(): SiteLayout[] {
  const d = useProjectStore.getState().data;
  return d.siteLayouts ?? (d.siteLayout ? [d.siteLayout] : []);
}

/**
 * Field-link geometry edits: compute the property updates the new position
 * allows, WITHOUT prompting (a field session can't reliably show a modal —
 * its default is KEEP, c6 contract). Blank fills only; conflicts are
 * dropped. Provenance rides along — the server allowlists
 * topology_json.topologyProvenance (same deploy batch; API deploys first),
 * so field fills are never indistinguishable from human input.
 */
export function computeTopologyUpdates(
  indicationId: string,
  geometry: GeoJSON.Geometry,
): Record<string, unknown> | null {
  const ind = useProjectStore.getState().data.indications.find((i) => i.id === indicationId);
  if (!ind) return null;
  const plan = planTopologyUpdate({ ...ind, geometry }, allLayouts());
  return plan?.updates ?? null;
}

/**
 * Re-evaluate topology for moved indications — ONE gesture, one dialog.
 * Blank fills (with provenance) apply immediately; conflicting stored
 * values are put to the user once, aggregated across the whole batch.
 *
 * MUST be called only on COMMITTED geometry changes — cancel paths are
 * inert by contract and never reach this function.
 */
export function reapplyTopologyForIndications(indicationIds: string[]): void {
  const store = useProjectStore.getState();
  const layouts = allLayouts();
  if (layouts.length === 0) return;

  const pending: { id: string; plan: TopologyApplyPlan }[] = [];
  let conflictCount = 0;

  for (const id of indicationIds) {
    const ind = store.data.indications.find((i) => i.id === id);
    if (!ind) continue;
    const plan = planTopologyUpdate(ind, layouts);
    if (!plan) continue;
    if (plan.updates) {
      store.updateIndication(id, { properties: plan.updates });
    }
    if (plan.conflicts.length > 0 && plan.conflictUpdates) {
      pending.push({ id, plan });
      conflictCount += plan.conflicts.length;
    }
  }

  if (pending.length === 0) return;

  const filledCount = indicationIds.length; // fills already applied above
  const sample = pending[0].plan.conflicts[0];
  useUIStore.getState().showConfirm({
    title: 'Moved faults — update values?',
    message:
      `Empty fields were filled from the new position either way. ` +
      `${conflictCount} stored value${conflictCount > 1 ? 's' : ''} on ` +
      `${pending.length} of ${filledCount} moved fault${filledCount > 1 ? 's' : ''} ` +
      `differ${conflictCount > 1 ? '' : 's'} from it (e.g. ${sample.field}: ` +
      `"${sample.stored}" → "${sample.derived}"). Update them, or keep the stored values?`,
    confirmLabel: 'Update',
    cancelLabel: 'Keep stored',
    onConfirm: () => {
      const s = useProjectStore.getState();
      for (const { id, plan } of pending) {
        if (plan.conflictUpdates) {
          s.updateIndication(id, { properties: plan.conflictUpdates });
        }
      }
    },
    onCancel: () => {
      // Keeping IS a human judgement about these specific values: the user
      // saw the derived value and declined it. Stamp the kept fields
      // 'human' so a later per-site bulk-accept never silently flips
      // values a person already defended (c6 review item 2).
      const s = useProjectStore.getState();
      for (const { id, plan } of pending) {
        const ind = s.data.indications.find((i) => i.id === id);
        if (!ind) continue;
        const patch = humanEditProvenance(
          ind.properties as unknown as Record<string, unknown>,
          plan.conflicts.map((c) => c.field),
          // a move-dialog Keep is also a verdict against a specific grid
          plan.result.kind === 'grid' ? plan.result.source : undefined,
        );
        s.updateIndication(id, { properties: patch });
      }
    },
  });
}

/** Single-indication convenience (drag of one mark). */
export function reapplyTopologyForIndication(indicationId: string): void {
  reapplyTopologyForIndications([indicationId]);
}
